import React, { useState, useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import './ChatInterface.css';

const ChatInterface = ({ group, messages, onSendMessage, currentUser }) => {
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (content, messageType, attachments) => {
    if (!content.trim()) return;
    
    setIsTyping(false);
    await onSendMessage(content, messageType, attachments);
  };

  const handleTyping = (isUserTyping) => {
    setIsTyping(isUserTyping);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('zh-TW', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('zh-TW', { 
        month: '2-digit', 
        day: '2-digit' 
      });
    }
  };

  const groupMembers = group?.member_count || 0;

  return (
    <div className="chat-interface">
      {/* Group Header */}
      <div className="chat-header">
        <div className="group-info">
          <div className="group-avatar">
            {group?.type === 'class' && '🏫'}
            {group?.type === 'club' && '🎯'}
            {group?.type === 'parent' && '👨‍👩‍👧‍👦'}
            {group?.type === 'general' && '📢'}
            {group?.type === 'academic' && '📚'}
            {group?.type === 'administrative' && '⚙️'}
          </div>
          
          <div className="group-details">
            <h3 className="group-name">{group?.name}</h3>
            <p className="group-description">{group?.description}</p>
            <div className="group-stats">
              <span className="member-count">{groupMembers} 成員</span>
              <span className="group-type">
                {group?.type === 'class' && '班級群組'}
                {group?.type === 'club' && '社團群組'}
                {group?.type === 'parent' && '家長群組'}
                {group?.type === 'general' && '一般群組'}
                {group?.type === 'academic' && '學術群組'}
                {group?.type === 'administrative' && '行政群組'}
              </span>
            </div>
          </div>
        </div>

        <div className="group-actions">
          <button className="btn-group-info" title="群組資訊">
            ℹ️
          </button>
          <button className="btn-group-members" title="群組成員">
            👥
          </button>
          <button className="btn-group-settings" title="群組設定">
            ⚙️
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="messages-container" ref={chatContainerRef}>
        {messages.length === 0 ? (
          <div className="no-messages">
            <div className="no-messages-content">
              <h3>開始對話</h3>
              <p>Start the conversation</p>
              <p className="group-welcome">
                歡迎來到 {group?.name} 群組！
                <br />
                Welcome to {group?.name} group!
              </p>
            </div>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((message, index) => {
              const isOwnMessage = message.sender_id === currentUser?.id;
              const showDate = index === 0 || 
                new Date(message.created_at).toDateString() !== 
                new Date(messages[index - 1]?.created_at).toDateString();
              
              return (
                <div key={message.id}>
                  {showDate && (
                    <div className="date-separator">
                      {new Date(message.created_at).toLocaleDateString('zh-TW', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  )}
                  
                  <MessageBubble
                    message={message}
                    isOwnMessage={isOwnMessage}
                    showTime={true}
                    formatTime={formatTime}
                  />
                </div>
              );
            })}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">正在輸入...</span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Composer */}
      <div className="message-composer-container">
        <MessageComposer
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          groupType={group?.type}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
};

export default ChatInterface;
